import React from 'react';

// ============================================================
// Filter chips / typology tree panel
// ============================================================

const { useState, useMemo, useEffect, useRef, useCallback } = React;

// Collapsible section wrapper for sidebar filters.
// Shows a header with title + selection count; body collapses when not expanded.
export function FilterAccordion({ title, hint, activeCount, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`fa ${open ? 'open' : ''}`}>
      <button type="button" className="fa-head" onClick={() => setOpen(!open)}>
        <span className="fa-title">{title}</span>
        {activeCount > 0 && <span className="fa-count">{activeCount}</span>}
        <span className="fa-chev" aria-hidden="true">
          <svg viewBox="0 0 10 10" width="10" height="10"><path d="M2 4l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </span>
      </button>
      {open && (
        <div className="fa-body">
          {hint && <div className="fa-hint">{hint}</div>}
          {children}
        </div>
      )}
    </div>
  );
}

// Country picker with search. Collapses long list; shows counts.
export function CountryFilter({ countries, selected, onToggle }) {
  const [q, setQ] = useState('');
  const filtered = q
    ? countries.filter(c => c.label.toLowerCase().includes(q.toLowerCase()))
    : countries;
  const sel = selected || new Set();
  return (
    <div className="country-filter">
      <input
        type="search" className="country-search"
        value={q} onChange={e => setQ(e.target.value)}
        placeholder="Search countries…"
      />
      <div className="country-list">
        {filtered.map(c => (
          <button
            key={c.name} type="button"
            className={`country-row ${sel.has(c.name) ? 'on' : ''}`}
            onClick={() => onToggle(c.name)}>
            <span className="country-row-label">{c.label}</span>
            <span className="country-row-count">{c.count}</span>
          </button>
        ))}
        {filtered.length === 0 && <div className="country-empty">No match</div>}
      </div>
    </div>
  );
}
export function TypologyPanel({ typology, selected, onToggle, onToggleMain, title, subCounts, mainCounts }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (name) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name); else n.add(name);
      return n;
    });
  };

  return (
    <div className="typology-panel">
      <div className="typology-title">{title}</div>
      <div className="typology-list">
        {typology.map(cat => {
          const subSelCount = cat.sub.filter(s => selected.has(s)).length;
          const isAllSub = subSelCount === cat.sub.length;
          const isMainSelected = selected.has(cat.name);
          const isPartial = subSelCount > 0 && !isAllSub;
          const isOpen = expanded.has(cat.name);
          const mainTotal = mainCounts ? (mainCounts[cat.name] || 0) : null;
          return (
            <div key={cat.name} className="typology-cat" style={{ '--cat-color': cat.color }}>
              <div className="typology-cat-row">
                <button
                  className={`typology-check ${isMainSelected || isAllSub ? 'checked' : ''} ${isPartial ? 'partial' : ''}`}
                  onClick={() => onToggleMain(cat)}
                  aria-label={`Select all in ${cat.name}`}
                >
                  {(isMainSelected || isAllSub) && <svg viewBox="0 0 16 16" width="11" height="11"><path d="M3 8.5l3 3 7-7.5" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  {isPartial && !isAllSub && <div className="partial-dot"/>}
                </button>
                <button className="typology-cat-name" onClick={() => toggleExpand(cat.name)}>
                  <span className="cat-dot" style={{ background: cat.color }} />
                  <span>{cat.name}</span>
                  <span className="cat-count">
                    {mainTotal !== null ? mainTotal : (subSelCount > 0 ? `${subSelCount}/${cat.sub.length}` : cat.sub.length)}
                  </span>
                  <svg className={`chev ${isOpen ? 'open' : ''}`} viewBox="0 0 12 12" width="10" height="10"><path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                </button>
              </div>
              {isOpen && (
                <div className="typology-subs">
                  {cat.sub.map(sub => {
                    const isSel = selected.has(sub);
                    const subN = subCounts ? (subCounts[sub] || 0) : null;
                    return (
                      <button
                        key={sub}
                        className={`typology-sub ${isSel ? 'selected' : ''}`}
                        onClick={() => onToggle(sub)}
                      >
                        <span className={`sub-check ${isSel ? 'checked' : ''}`}>
                          {isSel && <svg viewBox="0 0 16 16" width="9" height="9"><path d="M3 8.5l3 3 7-7.5" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </span>
                        <span>{sub}</span>
                        {subN !== null && <span className="sub-count">{subN}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SimpleFilter({ label, options, selected, onToggle, counts }) {
  return (
    <div className="simple-filter">
      <div className="simple-filter-label">{label}</div>
      <div className="simple-filter-opts">
        {options.map(opt => {
          const value = typeof opt === 'string' ? opt : opt.value;
          const text = typeof opt === 'string' ? opt : opt.label;
          const isSel = selected.has(value);
          return (
            <button
              key={value}
              className={`pill ${isSel ? 'selected' : ''}`}
              onClick={() => onToggle(value)}
            >
              {text}
              {counts && counts[value] !== undefined && <span className="pill-count">{counts[value]}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
